// Types importés depuis `preact` directement : les homonymes de l'espace de noms
// `JSX` sont dépréciés, et ceux de la surface de compatibilité React aussi.
import type { InputHTMLAttributes, SelectHTMLAttributes, TextareaHTMLAttributes } from 'preact';
import { useId, type ReactNode } from 'react';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select } from './ui/select';
import { Textarea } from './ui/textarea';

interface Commun {
  readonly libelle: string;
  /** Message d'erreur **en toutes lettres**, affiché sous le champ. */
  readonly erreur?: string | undefined;
  readonly aide?: string | undefined;
}

/**
 * Coque commune : libellé au-dessus, aide, commande, puis l'erreur en toutes
 * lettres. Une seule colonne, jamais deux champs côte à côte sur mobile.
 *
 * Le libellé est un vrai `<label for>`, pas un texte d'espace réservé — un
 * `placeholder` disparaît à la saisie et n'est lu par aucun lecteur d'écran.
 */
function Coque({
  id,
  libelle,
  aide,
  erreur,
  idAide,
  idErreur,
  children,
}: Commun & {
  readonly id: string;
  readonly idAide: string;
  readonly idErreur: string;
  readonly children: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={id}>{libelle}</Label>
      {aide !== undefined && (
        <p className="text-sm text-muted-foreground" id={idAide}>
          {aide}
        </p>
      )}
      {children}
      {erreur !== undefined && (
        <p className="text-sm font-medium text-destructive" id={idErreur}>
          {erreur}
        </p>
      )}
    </div>
  );
}

/** Identifiants des textes qui décrivent la commande, pour `aria-describedby`. */
function decrire(
  idAide: string,
  idErreur: string,
  aide: string | undefined,
  erreur: string | undefined,
): string {
  return [aide === undefined ? null : idAide, erreur === undefined ? null : idErreur]
    .filter((valeur): valeur is string => valeur !== null)
    .join(' ');
}

export type ProprietesChamp = Commun & Omit<InputHTMLAttributes, 'id' | 'aria-invalid'>;

export function Champ({ libelle, erreur, aide, ...reste }: ProprietesChamp) {
  const id = useId();
  const idErreur = `${id}-erreur`;
  const idAide = `${id}-aide`;
  const decrit = decrire(idAide, idErreur, aide, erreur);

  return (
    <Coque
      id={id}
      libelle={libelle}
      aide={aide}
      erreur={erreur}
      idAide={idAide}
      idErreur={idErreur}
    >
      <Input
        id={id}
        aria-invalid={erreur !== undefined}
        {...(decrit === '' ? {} : { 'aria-describedby': decrit })}
        {...reste}
      />
    </Coque>
  );
}

export type ProprietesZoneTexte = Commun & Omit<TextareaHTMLAttributes, 'id' | 'aria-invalid'>;

export function ZoneTexte({ libelle, erreur, aide, ...reste }: ProprietesZoneTexte) {
  const id = useId();
  const idErreur = `${id}-erreur`;
  const idAide = `${id}-aide`;
  const decrit = decrire(idAide, idErreur, aide, erreur);

  return (
    <Coque
      id={id}
      libelle={libelle}
      aide={aide}
      erreur={erreur}
      idAide={idAide}
      idErreur={idErreur}
    >
      <Textarea
        id={id}
        aria-invalid={erreur !== undefined}
        {...(decrit === '' ? {} : { 'aria-describedby': decrit })}
        {...reste}
      />
    </Coque>
  );
}

export type ProprietesChampListe = Commun & Omit<SelectHTMLAttributes, 'id' | 'aria-invalid'>;

/** Liste déroulante : même coque, même règle d'erreur, commande native. */
export function ChampListe({ libelle, erreur, aide, children, ...reste }: ProprietesChampListe) {
  const id = useId();
  const idErreur = `${id}-erreur`;
  const idAide = `${id}-aide`;
  const decrit = decrire(idAide, idErreur, aide, erreur);

  return (
    <Coque
      id={id}
      libelle={libelle}
      aide={aide}
      erreur={erreur}
      idAide={idAide}
      idErreur={idErreur}
    >
      <Select
        id={id}
        aria-invalid={erreur !== undefined}
        {...(decrit === '' ? {} : { 'aria-describedby': decrit })}
        {...reste}
      >
        {children}
      </Select>
    </Coque>
  );
}
